/**
 * PropertyReports — per-property reports view.
 *
 * Lists every report Auriel has generated for one property, filterable
 * by kind. Surfaces shortcuts to generate new reports of each kind:
 *
 *   - Traffic Report     → direct generate (uses GenerateReportButton)
 *   - Backlinks Report   → direct generate
 *   - Crawl Report       → links to the latest crawl detail page
 *                          (Crawl Reports are crawl-specific; can't be
 *                          generated from a generic property cursor —
 *                          each crawl is its own data unit)
 *
 * Routed at /p/:propertyId/reports — replaces the previous fall-through
 * to PropertyOverview that didn't show anything report-specific.
 */
import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { Card } from "@tremor/react";
import {
  Download, FileText, Globe, Link as LinkIcon, Search, Wrench,
  ArrowRight, ExternalLink,
} from "lucide-react";
import {
  useProperty, useReports, useCrawls, reportDownloadUrl,
  type Report, type ReportKind,
} from "@/lib/queries";
import GenerateReportButton from "@/components/GenerateReportButton";
import { cn } from "@/lib/utils";

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtBytes(n: number | null): string {
  if (n === null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const KIND_META: Record<
  ReportKind | "all",
  { label: string; Icon: React.ComponentType<{ className?: string }>; tone: string }
> = {
  all:        { label: "All",       Icon: FileText,  tone: "" },
  crawl:      { label: "Crawl",     Icon: Search,    tone: "bg-blue-500/10 text-blue-700 border-blue-500/30 dark:text-blue-300" },
  traffic:    { label: "Traffic",   Icon: Globe,     tone: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:text-emerald-300" },
  backlinks:  { label: "Backlinks", Icon: LinkIcon,  tone: "bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-300" },
  seo_audit:  { label: "Audit",     Icon: Wrench,    tone: "bg-violet-500/10 text-violet-700 border-violet-500/30 dark:text-violet-300" },
};


export default function PropertyReports() {
  const { propertyId: rawId } = useParams<{ propertyId: string }>();
  const propertyId = Number(rawId);
  const property = useProperty(propertyId);
  const [kindFilter, setKindFilter] = useState<ReportKind | "all">("all");

  const list = useReports({
    limit: 100,
    property_id: propertyId,
    kind: kindFilter === "all" ? null : kindFilter,
  });
  // Crawls list — used to surface "latest crawl" for the Crawl Report
  // shortcut. Limited (top 5) — the Crawl tab itself has the full list.
  const crawls = useCrawls(propertyId);

  if (property.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  if (!property.data) {
    return <div className="text-sm text-red-600">Property not found.</div>;
  }
  const p = property.data;

  // Per-kind counts on the unfiltered list — used for the pill badges
  // and the Generate-shortcut row. Only meaningful when kindFilter=all
  // because the API response is already filtered when a kind is set.
  // For exact per-kind counts under any filter we'd need a second
  // query; cheap workaround: show counts only when "All" is active.
  const allItems = list.data?.items ?? [];
  const countsByKind: Partial<Record<ReportKind, number>> =
    kindFilter === "all"
      ? allItems.reduce((acc, r) => {
          acc[r.report_kind] = (acc[r.report_kind] ?? 0) + 1;
          return acc;
        }, {} as Partial<Record<ReportKind, number>>)
      : {};

  // Latest completed crawl — link target for the Crawl Report shortcut.
  const latestCrawl = (crawls.data?.items ?? []).find(
    (c) => c.status === "completed" || c.status === "timed_out",
  );

  return (
    <div className="space-y-5">
      <div>
        <Link
          to={`/p/${propertyId}`}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          ← {p.domain}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">Reports</h1>
        <p className="text-sm text-muted-foreground">
          Customer-facing PDF reports generated from Auriel's substrate.
          Generate by kind from the buttons below or from the source page
          for each kind (Crawl detail, Search Console, Backlinks).
        </p>
      </div>

      {/* Generate shortcuts — Traffic + Backlinks generate directly here;
          Crawl points at the latest crawl detail page (each crawl is its
          own discrete report source). */}
      <Card className="p-4">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Generate a new report
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-emerald-600" />
            <GenerateReportButton propertyId={propertyId} kind="traffic" />
          </div>
          <div className="flex items-center gap-2">
            <LinkIcon className="h-4 w-4 text-amber-600" />
            <GenerateReportButton propertyId={propertyId} kind="backlinks" />
          </div>
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-blue-600" />
            {latestCrawl ? (
              <Link
                to={`/p/${propertyId}/crawl/${latestCrawl.id}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
              >
                <FileText className="h-3.5 w-3.5" />
                Generate Crawl Report
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ) : (
              <span className="text-xs text-muted-foreground italic">
                No completed crawl yet — start one to enable Crawl Reports
              </span>
            )}
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Crawl Reports are tied to a specific crawl run. Click through
          to a crawl's detail page to generate one (latest:{" "}
          {latestCrawl ? `#${latestCrawl.id}` : "none yet"}).
        </p>
      </Card>

      {/* Kind filter strip */}
      <div className="flex flex-wrap items-center gap-2">
        {(Object.keys(KIND_META) as Array<ReportKind | "all">).map((k) => {
          const meta = KIND_META[k];
          const Icon = meta.Icon;
          const active = kindFilter === k;
          const cnt = k === "all" ? allItems.length : countsByKind[k] ?? null;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setKindFilter(k)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium border transition-colors",
                active
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background hover:bg-accent",
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {meta.label}
              {kindFilter === "all" && cnt !== null && cnt > 0 && (
                <span className={cn(
                  "ml-1 rounded-full px-1.5 py-0.5 text-[10px] tabular-nums",
                  active
                    ? "bg-primary-foreground/20 text-primary-foreground"
                    : "bg-muted text-muted-foreground",
                )}>
                  {cnt}
                </span>
              )}
            </button>
          );
        })}
        <div className="ml-auto text-xs text-muted-foreground tabular-nums">
          {list.data && (
            <>{list.data.total} report{list.data.total === 1 ? "" : "s"} total</>
          )}
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        {list.isLoading && (
          <div className="px-4 py-6 text-sm text-muted-foreground">Loading…</div>
        )}
        {list.data?.items.length === 0 && (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-sm text-muted-foreground">
            <FileText className="h-10 w-10 opacity-50" />
            <p>
              {kindFilter === "all"
                ? `No reports yet for ${p.domain}.`
                : `No ${KIND_META[kindFilter].label.toLowerCase()} reports yet for ${p.domain}.`}
            </p>
            <p className="max-w-md text-center text-xs">
              Use the buttons above to generate one.
            </p>
          </div>
        )}

        {list.data && list.data.items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Kind</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Period</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Author</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((r) => (
                <ReportRow key={r.id} r={r} propertyId={propertyId} />
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}


function ReportRow({ r, propertyId }: { r: Report; propertyId: number }) {
  const meta = KIND_META[r.report_kind] ?? KIND_META.seo_audit;
  const Icon = meta.Icon;
  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/30">
      <td className="px-4 py-3 text-xs tabular-nums text-muted-foreground">
        {fmtDateTime(r.created_at)}
      </td>
      <td className="px-4 py-3">
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium",
            meta.tone,
          )}
        >
          <Icon className="h-3 w-3" />
          {meta.label}
        </span>
      </td>
      <td className="px-4 py-3 text-xs">
        {/* Source link — Crawl Reports point at the source crawl,
            others point at the property's relevant lens. Helps users
            audit "where did this number come from" without leaving
            the table. */}
        {r.report_kind === "crawl" && r.crawl_id ? (
          <Link
            to={`/p/${propertyId}/crawl/${r.crawl_id}`}
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            Crawl #{r.crawl_id}
            <ExternalLink className="h-3 w-3" />
          </Link>
        ) : r.report_kind === "traffic" ? (
          <Link
            to={`/p/${propertyId}/gsc`}
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            Search Console
            <ExternalLink className="h-3 w-3" />
          </Link>
        ) : r.report_kind === "backlinks" ? (
          <Link
            to={`/p/${propertyId}/backlinks`}
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            Backlinks
            <ExternalLink className="h-3 w-3" />
          </Link>
        ) : (
          <span className="text-muted-foreground italic">—</span>
        )}
      </td>
      <td className="px-4 py-3">{r.title}</td>
      <td className="px-4 py-3 text-xs text-muted-foreground">{r.period ?? "—"}</td>
      <td className="px-4 py-3 text-xs tabular-nums text-muted-foreground">
        {fmtBytes(r.file_size_bytes)}
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground">
        {r.created_by_email ?? <span className="italic">backfilled</span>}
      </td>
      <td className="px-4 py-3 text-right">
        <a
          href={reportDownloadUrl(r.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
        >
          <Download className="h-3.5 w-3.5" /> PDF
        </a>
      </td>
    </tr>
  );
}
